import { useState } from 'react';
import { FaEdit, FaTrash, FaUserShield, FaUser } from 'react-icons/fa';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';

export default function UserTable({ users, onUpdate }) {
  const [loading, setLoading] = useState(false);

  async function toggleAdmin(userId, currentRole) {
    if (window.confirm(`Are you sure you want to ${currentRole === 'admin' ? 'remove admin' : 'make admin'}?`)) {
      setLoading(true);
      try {
        await updateDoc(doc(db, 'users', userId), {
          role: currentRole === 'admin' ? 'user' : 'admin',
          isAdmin: currentRole !== 'admin',
        });
        toast.success('User role updated successfully');
        onUpdate();
      } catch (error) {
        console.error('Error updating user:', error);
        toast.error('Failed to update user role');
      } finally {
        setLoading(false);
      }
    }
  }

  async function handleDelete(userId) {
    if (window.confirm('Are you sure you want to delete this user? This will also delete all their listings.')) {
      setLoading(true);
      try {
        await deleteDoc(doc(db, 'users', userId));
        toast.success('User deleted successfully');
        onUpdate();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      } finally {
        setLoading(false);
      }
    }
  }

  if (!users || users.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <p className="text-gray-500">No users found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      {user.photoURL ? (
                        <img
                          className="h-10 w-10 rounded-full"
                          src={user.photoURL}
                          alt=""
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <FaUser className="text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{user.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.role === 'admin' || user.isAdmin
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role === 'admin' || user.isAdmin ? (
                      <>
                        <FaUserShield className="mr-1" /> Admin
                      </>
                    ) : (
                      <>
                        <FaUser className="mr-1" /> User
                      </>
                    )}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.timestamp?.toDate().toLocaleDateString() || 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleAdmin(user.id, user.role)}
                      disabled={loading}
                      className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                      title={user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                    >
                      <FaUserShield />
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      disabled={loading}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
